<template>
  <v-layout
    align-center
    justify-center
  >
    <v-flex
      xs12
      sm8
      md6
      lg4
    >
      <v-card class="elevation-12 mt-16">
        <v-toolbar
          dark
          color="primary"
        >
          <v-spacer />
          <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
          <v-spacer />
        </v-toolbar>
        <div v-if="step === 'login'">
          <v-card-text>
            <v-form ref="formLogin">
              <v-text-field
                prepend-icon="mdi-account"
                v-model="details.email"
                :label="$t('lbl.email')"
                type="email"
                required
                :rules="rules.emailRules"
              />
              <v-text-field
                prepend-icon="mdi-lock"
                v-model="details.password"
                :label="$t('lbl.password')"
                :type="show ? 'text' : 'password'"
                required
                :rules="rules.passwordRules"
              >
                <template v-slot:append>
                  <v-icon
                    color="red"
                    @click="show = !show"
                  >{{ show ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </template>
              </v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              class="text-none"
              color="info"
              @click="formStep('forgot')"
            >{{ $t('lbl.forgotPassword') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading=loading
              @click.native="loginUser"
            >{{ $t("lbl.login") }}</v-btn>
          </v-card-actions>
        </div>

        <div v-if="step === 'forgot'">
          <v-card-text>
            <v-form ref="formForgot">
              <v-text-field
                prepend-icon="mdi-email"
                v-model="user.email"
                :label="$t('lbl.email')"
                type="email"
                required
                :rules="rules.emailRules"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              color="primary"
              @click="formStep('login')"
            >{{ $t('lbl.cancel') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading=loading
              @click="send"
            >{{ $t('lbl.sendCode') }}</v-btn>
          </v-card-actions>
        </div>

        <div v-if="step === 'reset'">
          <v-card-text>
            <v-form ref="formReset">
              <v-text-field
                autocomplete="new-password"
                prepend-icon="mdi-form-textbox-password"
                v-model="user.verify"
                :label="$t('lbl.verifyCode')"
                required
                :rules="rules.verifyRules"
              />
              <v-text-field
                autocomplete="new-password"
                prepend-icon="mdi-lock"
                v-model="user.password"
                :label="$t('lbl.newPassword')"
                :type="show ? 'text' : 'password'"
                required
                :rules="rules.passwordRules"
              >
                <template v-slot:append>
                  <v-icon
                    color="red"
                    @click="show = !show"
                  >{{ show ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </template>
              </v-text-field>
              <v-text-field
                autocomplete="new-password"
                prepend-icon="mdi-lock"
                v-model="user.confirmPassword"
                :label="$t('lbl.confirmPassword')"
                :type="show ? 'text' : 'password'"
                required
                :rules="confirmPassword"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              color="primary"
              @click="formStep('login')"
            >{{ $t('lbl.cancel') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              @click="reset"
            >{{ $t('lbl.save') }}</v-btn>
          </v-card-actions>
        </div>

        <div v-if="step === 'twofa'">
          <v-card-text>
            <v-form ref="formTwofa">
              <v-text-field
                prepend-icon="mdi-form-textbox-password"
                v-model="details.verify"
                :label="$t('lbl.twofaCode')"
                required
                :rules="rules.verifyRules"
              />
              <!-- hidden input field added due to form submit bug with only one input -->
              <!-- https://github.com/vuetifyjs/vuetify/issues/3032 -->
              <!-- https://github.com/vuetifyjs/vuetify/issues/4302 -->
              <v-text-field v-show="false" />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              color="primary"
              @click="formStep('login')"
            >{{ $t('lbl.cancel') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              @click="loginTwofa"
            >{{ $t('lbl.verify') }}</v-btn>
          </v-card-actions>
        </div>

        <div v-if="step === 'twofaCode'">
          <v-card-title />
          <v-card-text class="text-center">
            <span v-html="$t('lbl.twofaHelp')" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="formStep('twofa')"
            >{{ $t('lbl.ok') }}</v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import {
  required,
  email,
  otpcode,
  minLength,
  maxLength,
  nospace,
  password,
  passwordMatch,
} from "../common/validation";

export default {
  data() {
    return {
      show: false,
      step: "login",
      loading: false,
      details: {
        email: "",
        password: "",
        verify: "",
        lang: this.$vuetify.lang.current,
      },
      user: {
        email: "",
        verify: "",
        password: "",
        confirmPassword: "",
      },
      rules: {
        emailRules: [required, email],
        verifyRules: [required, otpcode],
        passwordRules: [
          required,
          nospace,
          minLength(8),
          maxLength(32),
          password,
        ],
      },
    };
  },
  computed: {
    confirmPassword() {
      return [required, passwordMatch(this.user.password)];
    },
    formTitle() {
      let result = "";
      switch (this.step) {
        case "login":
          result = this.$t("lbl.signin");
          break;
        case "twofa":
          result = this.$t("lbl.twofa");
          break;
        case "twofaCode":
          result = this.$t("lbl.twofaCode");
          break;
        case "forgot":
          result = this.$t("lbl.forgotPassword");
          break;
        case "reset":
          result = this.$t("lbl.resetPassword");
          break;
        default:
          break;
      }
      return result;
    },
  },
  watch: {
    "user.password": {
      handler() {
        this.$nextTick(() => {
          this.$refs.formReset.validate();
        });
      },
    },
    "user.confirmPassword": {
      handler() {
        this.$nextTick(() => {
          this.$refs.formReset.validate();
        });
      },
    },
  },
  methods: {
    ...mapActions("auth", ["login", "forgotPassword", "resetPassword"]),
    loginUser() {
      const isValid = this.$refs.formLogin.validate(true);
      if (!isValid) {
        return;
      }
      this.loading = true;
      this.login(this.details)
        .then((data) => {
          if (data.twofa) {
            this.formStep("twofa");
          } else {
            this.formStep("twofaCode");
          }
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        }).finally(() => {
          this.loading = false;
        });
    },

    loginTwofa() {
      const isValid = this.$refs.formTwofa.validate(true);
      if (!isValid) {
        return;
      }
      this.login(this.details)
        .then(() => {
          this.$router.push("/");
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    send() {
      const isValid = this.$refs.formForgot.validate(true);
      if (!isValid) {
        return;
      }
      this.loading = true;
      this.forgotPassword({
        email: this.user.email,
        lang: this.$vuetify.lang.current,
      })
        .then(() => {
          this.formStep("reset");
          this.$toasted.success(
            this.$t("msg.send", { item: this.$t("lbl.verifyCode") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    reset() {
      const isValid = this.$refs.formReset.validate(true);
      if (!isValid) {
        return;
      }
      this.resetPassword({
        email: this.user.email,
        verify: this.user.verify,
        password: this.user.password,
      })
        .then(() => {
          this.$router.push("/");
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    formStep(step) {
      switch (step) {
        case "forgot":
          this.step = "forgot";
          this.$nextTick(() => {
            this.$refs.formForgot.reset();
          });
          break;
        case "reset":
          this.step = "reset";
          this.$nextTick(() => {
            this.$refs.formReset.reset();
          });
          break;
        case "login":
          this.show = false;
          this.details.verify = "";
          this.step = "login";
          this.$nextTick(() => {
            this.$refs.formLogin.reset();
          });
          break;
        case "twofa":
          this.step = "twofa";
          this.$nextTick(() => {
            this.$refs.formTwofa.reset();
          });
          break;
        case "twofaCode":
          this.step = "twofaCode";
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
.v-toolbar__title {
  font-size: 1.5rem !important;
}
</style>
